import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDashboardStats } from '../hooks/useStats';
import ImportModal from '../components/ImportModal';
import QuickAddPhotoModal from '../components/QuickAddPhotoModal';
import LancerInventaireModal from '../components/LancerInventaireModal';
import PrintCodesModal from '../components/PrintCodesModal';
import ActivityFeed from '../components/ActivityFeed';

const DashboardPage: React.FC = () => {
  const { data: stats, isLoading, error } = useDashboardStats();
  const navigate = useNavigate();
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [isInventaireModalOpen, setIsInventaireModalOpen] = useState(false);
  const [isPrintCodesModalOpen, setIsPrintCodesModalOpen] = useState(false);
  
  console.log('🎯 DashboardPage - Stats:', stats);
  console.log('🎯 DashboardPage - Loading:', isLoading);
  console.log('🎯 DashboardPage - Error:', error);

  // Navigation handlers
  const handleNavigateToObjets = (filter?: string) => {
    const params = filter ? `?filter=${encodeURIComponent(filter)}` : '';
    navigate(`/inventaire${params}`);
  };

  const handleNavigateToZones = (filter?: string) => {
    const params = filter ? `?filter=${encodeURIComponent(filter)}` : '';
    navigate(`/lieux${params}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Tableau de Bord</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white overflow-hidden shadow rounded-lg animate-pulse">
              <div className="p-5">
                <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-6 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Tableau de Bord</h1>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-red-800 font-medium">Erreur de chargement</h3>
          <p className="text-red-600 mt-1">Impossible de charger les statistiques.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Tableau de Bord</h1>
        <button
          onClick={() => setIsImportModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm"
        >
          <span className="text-lg">📥</span>
          <span className="font-medium">Importer des données</span>
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div 
          className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => handleNavigateToObjets()}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">📦</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Objets en stock</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats?.objets.total || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div 
          className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => handleNavigateToObjets('etat:A_VERIFIER')}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">⚠️</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">À vérifier</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats?.objets.parEtat['A_VERIFIER'] || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div 
          className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => handleNavigateToObjets('besoinAchat:true')}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">🛒</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Besoin d'achat</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats?.objets.besoinAchat || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div 
          className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => handleNavigateToObjets('sansPhoto:true')}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">📸</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Sans photo</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats?.objets.sansPhoto || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques détaillées */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Objets par état */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Objets par état</h3>
            <div className="space-y-2">
              {stats?.objets.parEtat && Object.entries(stats.objets.parEtat).map(([etat, count]) => (
                <div 
                  key={etat} 
                  className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                  onClick={() => handleNavigateToObjets(`etat:${etat}`)}
                >
                  <span className="text-sm text-gray-600">{etat}</span>
                  <span className="text-sm font-medium text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Zones et boxes */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Zones et Boxes</h3>
            <div className="space-y-2">
              <div 
                className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                onClick={() => handleNavigateToZones()}
              >
                <span className="text-sm text-gray-600">Zones totales</span>
                <span className="text-sm font-medium text-gray-900">{stats?.zones.total || 0}</span>
              </div>
              <div 
                className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                onClick={() => handleNavigateToZones('avecTheme:true')}
              >
                <span className="text-sm text-gray-600">Zones avec thème</span>
                <span className="text-sm font-medium text-gray-900">{stats?.zones.avecTheme || 0}</span>
              </div>
              <div 
                className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                onClick={() => handleNavigateToZones('type:boxes')}
              >
                <span className="text-sm text-gray-600">Boxes totales</span>
                <span className="text-sm font-medium text-gray-900">{stats?.boxes.total || 0}</span>
              </div>
              <div 
                className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                onClick={() => handleNavigateToZones('horsDomicile:true')}
              >
                <span className="text-sm text-gray-600">Boxes hors domicile</span>
                <span className="text-sm font-medium text-gray-900">{stats?.boxes.horsDomicile || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold text-gray-800">Actions Rapides</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button 
            onClick={() => setIsPhotoModalOpen(true)}
            className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
          >
            <span className="text-xl">📸</span>
            <span>Ajouter un objet (Photo)</span>
          </button>
          
          <button 
            onClick={() => setIsPrintCodesModalOpen(true)}
            className="flex items-center justify-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 transition-colors"
          >
            <span className="text-xl">🖨️</span>
            <span>Imprimer des codes</span>
          </button>
          
          <button 
            onClick={() => setIsInventaireModalOpen(true)}
            className="flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors"
          >
            <span className="text-xl">📋</span>
            <span>Lancer un inventaire</span>
          </button>
          
          <button
            onClick={() => navigate('/inventaire', { state: { openCreateForm: true } })}
            className="flex items-center justify-center gap-2 bg-gray-600 text-white px-6 py-3 rounded-md hover:bg-gray-700 transition-colors"
          >
            <span className="text-xl">✏️</span>
            <span>Créer un nouvel objet</span>
          </button>
        </div>
      </div>

      {/* Activités Récentes */}
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold text-gray-800">Activités Récentes</h2>
        <ActivityFeed limit={10} />
      </div>

      {/* Modals */}
      <ImportModal 
        isOpen={isImportModalOpen} 
        onClose={() => setIsImportModalOpen(false)} 
      />
      
      <QuickAddPhotoModal 
        isOpen={isPhotoModalOpen} 
        onClose={() => setIsPhotoModalOpen(false)} 
      />
      
      <LancerInventaireModal 
        isOpen={isInventaireModalOpen} 
        onClose={() => setIsInventaireModalOpen(false)} 
      />
      
      <PrintCodesModal 
        isOpen={isPrintCodesModalOpen} 
        onClose={() => setIsPrintCodesModalOpen(false)} 
      />
    </div>
  );
};

export default DashboardPage;